import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Loader2, Eye, LogOut, TrendingUp, Users, Clock, CheckCircle, Settings, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'Nouveau', variant: 'default', color: 'bg-[rgba(51,109,191,0.12)] text-[rgb(51,109,191)] border-[rgba(51,109,191,0.22)]' },
  in_progress: { label: 'En cours', variant: 'secondary', color: 'bg-[rgba(168,179,156,0.18)] text-[rgb(62,107,90)] border-[rgba(168,179,156,0.28)]' },
  signed: { label: 'Signé', variant: 'default', color: 'bg-[rgba(47,111,143,0.16)] text-[rgb(47,111,143)] border-[rgba(47,111,143,0.24)]' },
  lost: { label: 'Perdu', variant: 'destructive', color: 'bg-[rgba(139,58,58,0.12)] text-[rgb(139,58,58)] border-[rgba(139,58,58,0.20)]' }
};

export const AdminLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [notesUpdate, setNotesUpdate] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Filters
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('');

  useEffect(() => {
    // Verify session on mount
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSession = async () => {
    try {
      // Check if user is authenticated via cookie
      await api.get(`${API_BASE}/admin/me`, { withCredentials: true });
      // If successful, load data
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Session check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchLeads = async () => {
    try {
      const params = {};
      if (serviceFilter !== 'all') params.service = serviceFilter;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (cityFilter) params.city = cityFilter;

      const response = await api.get(`${API_BASE}/admin/leads`, {
        params,
        withCredentials: true
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Erreur lors du chargement des leads');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(`${API_BASE}/admin/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setStatusUpdate(lead.status);
    setNotesUpdate(lead.notes || '');
    setDialogOpen(true);
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    setUpdating(true);

    try {
      await api.patch(
        `${API_BASE}/admin/leads/${selectedLead.id}`,
        {
          status: statusUpdate,
          notes: notesUpdate
        },
        { withCredentials: true }
      );

      toast.success('Lead mis à jour avec succès');
      setDialogOpen(false);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post(`${API_BASE}/admin/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8" data-testid="admin-leads-page">
      <div className="mx-auto w-full max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              Dashboard Admin
            </h1>
            <p className="text-muted-foreground mt-1">Gestion des leads Homely Solutions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/cms')} data-testid="cms-button">
              <FileText className="mr-2 h-4 w-4" />
              CMS
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/settings')} data-testid="settings-button">
              <Settings className="mr-2 h-4 w-4" />
              Paramètres
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card data-testid="stat-card-total">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-card-new">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Nouveaux</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.by_status.new}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-card-progress">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">En cours</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.by_status.in_progress}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-card-signed">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Signés</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.by_status.signed}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Service</Label>
                <Select value={serviceFilter} onValueChange={(val) => { setServiceFilter(val); setTimeout(fetchLeads, 100); }}>
                  <SelectTrigger data-testid="filter-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="louer">Louer</SelectItem>
                    <SelectItem value="vendre">Vendre</SelectItem>
                    <SelectItem value="renover">Rénover</SelectItem>
                    <SelectItem value="clients">Clients</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setTimeout(fetchLeads, 100); }}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="signed">Signé</SelectItem>
                    <SelectItem value="lost">Perdu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  placeholder="Filtrer par ville"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  data-testid="filter-city"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchLeads} className="w-full" data-testid="apply-filters-button">
                  Appliquer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card data-testid="leads-table-card">
          <CardHeader>
            <CardTitle>Leads ({leads.length})</CardTitle>
            <CardDescription>Liste complète des demandes reçues</CardDescription>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            <div className="overflow-x-auto">
              <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Qualification</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Aucun lead trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} data-testid="lead-row">
                      <TableCell className="text-sm">
                        {new Date(lead.created_at).toLocaleDateString('fr-CH')}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{lead.contact.name}</div>
                        <div className="text-xs text-muted-foreground">{lead.contact.email}</div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize">{lead.service}</span>
                      </TableCell>
                      <TableCell>{lead.city || '-'}</TableCell>
                      <TableCell>
                        {/* Display key qualification info */}
                        <div className="text-xs space-y-1">
                          {lead.answers?.budget && (
                            <div><span className="font-medium">Budget:</span> {lead.answers.budget}</div>
                          )}
                          {lead.answers?.delai && (
                            <div><span className="font-medium">Délai:</span> {lead.answers.delai}</div>
                          )}
                          {lead.answers?.delai_projet && (
                            <div><span className="font-medium">Délai:</span> {lead.answers.delai_projet}</div>
                          )}
                          {lead.answers?.objectif_revenu && (
                            <div><span className="font-medium">Objectif:</span> {lead.answers.objectif_revenu}</div>
                          )}
                          {lead.answers?.budget_mensuel && (
                            <div><span className="font-medium">Budget:</span> {lead.answers.budget_mensuel}</div>
                          )}
                          {!lead.answers?.budget && !lead.answers?.delai && !lead.answers?.delai_projet && !lead.answers?.objectif_revenu && !lead.answers?.budget_mensuel && (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig[lead.status]?.color || ''} data-testid="lead-status-badge">
                          {statusConfig[lead.status]?.label || lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewLead(lead)}
                          data-testid="view-lead-button"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lead Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle>Détails du lead</DialogTitle>
            <DialogDescription>
              {selectedLead && `Lead ID: ${selectedLead.id}`}
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Nom</Label>
                  <p>{selectedLead.contact.name}</p>
                </div>
                <div>
                  <Label className="font-semibold">Email</Label>
                  <p>{selectedLead.contact.email}</p>
                </div>
                <div>
                  <Label className="font-semibold">Téléphone</Label>
                  <p>{selectedLead.contact.phone}</p>
                </div>
                <div>
                  <Label className="font-semibold">Ville</Label>
                  <p>{selectedLead.city}</p>
                </div>
                <div>
                  <Label className="font-semibold">Service</Label>
                  <p className="capitalize">{selectedLead.service}</p>
                </div>
                <div>
                  <Label className="font-semibold">Pôle</Label>
                  <p className="capitalize">{selectedLead.pole || '-'}</p>
                </div>
              </div>

              <div>
                <Label className="font-semibold">Réponses du formulaire</Label>
                {/* Display key qualification fields prominently */}
                {(selectedLead.answers.budget || selectedLead.answers.delai || selectedLead.answers.objectif_revenu || selectedLead.answers.budget_mensuel) && (
                  <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                    {selectedLead.answers.budget && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Budget</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.budget}</p>
                      </div>
                    )}
                    {selectedLead.answers.delai && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Délai</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.delai}</p>
                      </div>
                    )}
                    {selectedLead.answers.delai_projet && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Délai projet</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.delai_projet}</p>
                      </div>
                    )}
                    {selectedLead.answers.delai_lancement && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Délai lancement</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.delai_lancement}</p>
                      </div>
                    )}
                    {selectedLead.answers.objectif_revenu && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Objectif revenu</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.objectif_revenu}</p>
                      </div>
                    )}
                    {selectedLead.answers.budget_mensuel && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Budget mensuel</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.budget_mensuel}</p>
                      </div>
                    )}
                    {selectedLead.answers.valeur_estimee && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Valeur estimée</p>
                        <p className="text-sm font-semibold text-blue-900">{selectedLead.answers.valeur_estimee}</p>
                      </div>
                    )}
                  </div>
                )}
                {/* All answers in collapsible format */}
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                    Voir toutes les réponses (JSON)
                  </summary>
                  <pre className="bg-muted p-3 rounded text-xs mt-2 max-h-40 overflow-y-auto">
                    {JSON.stringify(selectedLead.answers, null, 2)}
                  </pre>
                </details>
              </div>

              <div>
                <Label htmlFor="status-update">Statut</Label>
                <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                  <SelectTrigger id="status-update" data-testid="update-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="signed">Signé</SelectItem>
                    <SelectItem value="lost">Perdu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes-update">Notes internes</Label>
                <Textarea
                  id="notes-update"
                  value={notesUpdate}
                  onChange={(e) => setNotesUpdate(e.target.value)}
                  placeholder="Ajouter des notes..."
                  rows={4}
                  data-testid="update-notes-textarea"
                />
              </div>

              <Button
                onClick={handleUpdateLead}
                disabled={updating}
                className="w-full"
                style={{ backgroundColor: 'var(--cta-blue)' }}
                data-testid="save-lead-button"
              >
                {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {updating ? 'Mise à jour...' : 'Enregistrer'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
